import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { AnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown, Sortable } from '@typings';
import { CSSProperties } from 'react';

import { Counter } from '../Counter';

import { SortableItemHandle } from './SortableItemHandle';

interface Props {
  childCount?: number;
  className?: string;
  depth: number;
  dragMode?: Sortable.DragMode;
  handlePosition?: Sortable.HandlePosition;
  id: Sortable.Id;
  label?: string;
  indentationWidth: number;
  isDisabled?: boolean;
  isDragOverlay?: boolean;
  status?: Sortable.ItemStatus;
  menuItems?: Dropdown.MenuItem[];
  render: (id: Sortable.Id) => JSX.Element;
}

const animateLayoutChanges: AnimateLayoutChanges = ({ isSorting, wasDragging }) => !(isSorting || wasDragging);

export const SortableItem = ({
  childCount = 0,
  className,
  depth,
  dragMode = 'item',
  handlePosition = 'middle',
  id,
  label,
  indentationWidth,
  isDisabled,
  isDragOverlay = false,
  status,
  render,
  menuItems,
}: Props) => {
  const { isDragging, listeners, setDraggableNodeRef, setDroppableNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    animateLayoutChanges,
    disabled: isDisabled,
    id,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <li ref={setDroppableNodeRef} className={cn('list-none', className)}>
      <div ref={setDraggableNodeRef} style={style} className="relative">
        <div
          className={cn(
            'relative flex gap-2 overflow-hidden rounded-regular bg-white p-2 transition-colors focus-within:bg-neutral-98 hover:bg-neutral-98',
            {
              'bg-neutral-98': isDragging || isDragOverlay,
              'cursor-auto': isDisabled,
              'cursor-grab': dragMode === 'item' && !isDisabled,
              'items-center': handlePosition === 'middle',
              'items-end': handlePosition === 'end',
            },
          )}
          style={{ marginLeft: `${indentationWidth * depth}px` }}
          {...(dragMode === 'item' && listeners)}
        >
          {isDefined(status) && (
            <div
              className={cn('absolute bottom-0 left-0 top-0 w-1', {
                'bg-error-45': status === 'error',
                'bg-warning-48': status === 'warning',
              })}
            />
          )}
          {isDragging && <div className="absolute inset-0 z-elevated rounded-regular bg-neutral-90" />}
          {!isDisabled && (
            <SortableItemHandle
              ref={setActivatorNodeRef}
              isDragging={isDragging}
              label={label ?? id.toString()}
              menuItems={menuItems}
              {...(dragMode === 'handle' && { listeners })}
            />
          )}
          {render(id)}
        </div>
        {childCount > 0 && (
          <div className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2">
            <Counter content={childCount + 1} />
          </div>
        )}
      </div>
    </li>
  );
};
